import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/Button';
import { MaterialIcon } from '@/components/MaterialIcon';
import { Controller, useForm } from 'react-hook-form';
import Autocomplete from '@/components/Autocomplete';
import useFetchCompaniesMasterData from '@/features/agent/companies/api/useFetchCompaniesMasterData';
import type AgentApiCompaniesMasterData from '@/types/serializers/AgentApiCompaniesMasterData';
import { LISTED_PHASE_ITEMS } from '../constants';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onDecide: (item: AgentApiCompaniesMasterData[][number]) => void;
};

export default function CompaniesDatabaseImportModal({ isOpen, onClose, onDecide }: Props) {
  const [inputQuery, setInputQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<AgentApiCompaniesMasterData[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [mode, setMode] = useState<'search' | 'preview'>('search');
  const { control, watch, reset } = useForm<{ selectedId?: string }>();
  const descRef = useRef<HTMLDivElement | null>(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const [descOverflowed, setDescOverflowed] = useState(false);
  const [lineHeightPx, setLineHeightPx] = useState(0);

  // モーダルの開閉に応じて、検索語・結果・プレビューモード・テキスト展開状態・選択値などを初期化する
  useEffect(() => {
    if (!isOpen) {
      setInputQuery('');
      setDebouncedQuery('');
      setResults([]);
      setSearchCompleted(false);
      setMode('search');
      setDescExpanded(false);
      setDescOverflowed(false);
      reset({ selectedId: undefined });
    }
  }, [isOpen, reset]);

  // 入力中のタイプに対して即時検索を走らせないための1秒デバウンス
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(inputQuery);
    }, 1000);
    return () => clearTimeout(t);
  }, [inputQuery]);

  const { data, isFetching } = useFetchCompaniesMasterData({ query: debouncedQuery });

  // 検索語（debouncedQuery）の変化やフェッチ状況に応じて、検索結果・ローディング・完了フラグを反映する
  useEffect(() => {
    if (!isOpen) return;
    if (!debouncedQuery) {
      setResults([]);
      setSearchCompleted(false);
      setLoading(false);
      return;
    }
    setLoading(isFetching);
    const recs = (data ?? []) as AgentApiCompaniesMasterData[];
    setResults(recs);
    if (!isFetching) setSearchCompleted(true);
  }, [data, isFetching, debouncedQuery, isOpen]);

  // プレビュー表示時のみ、事業内容の行数（3行超）を監視して「もっと見る」表示可否を切り替える
  useEffect(() => {
    if (mode !== 'preview') return;
    const el = descRef.current;
    if (!el) return;
    const update = () => {
      const cs = window.getComputedStyle(el);
      const lh = parseFloat(cs.lineHeight || '0');
      setLineHeightPx(lh);
      const overflowed = lh > 0 ? el.scrollHeight > lh * 3 + 1 : false;
      setDescOverflowed(overflowed);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [mode, results]);

  const selectedId = watch('selectedId');
  const selectedIdNumber = useMemo(() => (selectedId ? Number(selectedId) : null), [selectedId]);
  const selected = useMemo(
    () => results.find((r) => r.id === selectedIdNumber) || null,
    [results, selectedIdNumber]
  );

  const selectItems = useMemo(
    () =>
      results.map((r) => ({
        label: r.name,
        value: String(r.id),
        description: r.address || '',
      })),
    [results]
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent
        className={`w-full ${mode === 'preview' ? 'max-w-[1094px]' : 'max-w-screen-sm'} flex max-h-[687px] flex-col rounded-sm`}
      >
        <DialogTitle className="border-b border-solid border-gray-200 px-6 py-4 text-lg! font-bold!">
          データベースから読み込む
        </DialogTitle>
        {mode === 'search' ? (
          <>
            <div className="flex-1 overflow-y-auto p-6">
              <p className="text-sm">
                企業情報をデータベースから読み込むことができます。企業名で検索してください。
              </p>

              <div className="mt-2 max-h-64 overflow-y-auto rounded-sm">
                <Controller
                  control={control}
                  name="selectedId"
                  render={({ field: { value, onChange } }) => (
                    <div className="mt-4">
                      <Autocomplete
                        items={selectItems}
                        value={value}
                        onChange={(v) => onChange(v)}
                        onInputChange={(q) => setInputQuery(q)}
                        placeholder="企業名で検索"
                      />
                    </div>
                  )}
                />
              </div>
              {!loading && searchCompleted && results.length === 0 && debouncedQuery && (
                <div className="mt-2 flex items-center text-sm text-sub">
                  <MaterialIcon name="warning" className="mr-2 text-xl! text-[#D19100]" />
                  企業データが存在しません。手動で新規作成してください。
                </div>
              )}
            </div>
            <div className="flex justify-end gap-4 border-t border-solid border-gray-200 px-6 py-4">
              <Button color="secondary" size="md" onClick={onClose}>
                キャンセル
              </Button>
              <Button
                color="primary"
                size="md"
                onClick={() => selected && setMode('preview')}
                disabled={!selected}
              >
                決定
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6">
              {selected && (
                <div className="text-main">
                  <h3 className="mb-7 text-xl font-bold">{selected.name}</h3>
                  <div className="mb-4 text-sm whitespace-pre-line">
                    <div className="text-sm leading-normal font-bold">事業内容</div>
                    <div
                      ref={descRef}
                      className="mt-2 text-sm leading-[1.7] whitespace-pre-line"
                      style={{
                        maxHeight:
                          descExpanded || !lineHeightPx ? undefined : `${lineHeightPx * 3}px`,
                        overflow: descExpanded ? 'visible' : 'hidden',
                      }}
                    >
                      {selected.description || '—'}
                    </div>
                    {descOverflowed && (
                      <button
                        type="button"
                        className="flex w-full items-center justify-center rounded-sm p-3 hover:bg-gray-50"
                        onClick={() => setDescExpanded((v) => !v)}
                      >
                        {descExpanded ? (
                          <MaterialIcon name="keyboard_arrow_up" className="text-base!" />
                        ) : (
                          <MaterialIcon name="keyboard_arrow_down" className="text-base!" />
                        )}
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-[46px] gap-y-7">
                    <div>
                      <div className="text-sm leading-normal font-bold">上場情報</div>
                      <div className="mt-2 text-sm leading-[1.7]">
                        {selected.listed_phase
                          ? (LISTED_PHASE_ITEMS.find((i) => i.value === selected.listed_phase)
                              ?.label ?? 'NG')
                          : '未入力'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm leading-normal font-bold">設立</div>
                      <div className="mt-2 text-sm leading-[1.7]">
                        {selected.established_info || '未入力'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm leading-normal font-bold">従業員数</div>
                      <div className="mt-2 text-sm leading-[1.7]">
                        {selected.number_of_employee || '未入力'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm leading-normal font-bold">住所</div>
                      <div className="mt-2 text-sm leading-[1.7]">
                        {selected.address || '未入力'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm leading-normal font-bold">業種</div>
                      <div className="mt-2 text-sm leading-[1.7]">
                        {selected.industry_name || '未入力'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm leading-normal font-bold">URL</div>
                      <div className="mt-2 text-sm leading-[1.7]">
                        {selected.official_url ? (
                          <a
                            className="underline"
                            href={selected.official_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {selected.official_url}
                          </a>
                        ) : (
                          '未入力'
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-4 border-t border-solid border-gray-200 px-6 py-4">
              <Button color="secondary" size="md" onClick={onClose}>
                キャンセル
              </Button>
              <Button
                color="primary"
                size="md"
                onClick={() => selected && onDecide(selected)}
                disabled={!selected}
              >
                読み込む
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
